import type { ReactNode } from "react"
import { Box, Collapse, Paper, Stack, Typography } from "@mui/material"
import { alpha } from "@mui/material/styles"
import CheckIcon from '@mui/icons-material/Check'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import type { PassoStatus } from "./passo-utils"

interface StepAccordionItemProps {
    ordem: number | string
    titulo: string
    regra: string
    apoio?: string
    status: PassoStatus
    selecionados: number
    onClick: () => void
    children?: ReactNode
}

function Badge({ status, ordem }: { status: PassoStatus; ordem: number | string }): JSX.Element {
    const concluido = status === 'concluido'
    const bloqueado = status === 'bloqueado'

    return (
        <Box
            sx={{
                width: 32,
                height: 32,
                flexShrink: 0,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 700,
                color: concluido ? 'primary.contrastText' : bloqueado ? 'text.disabled' : 'text.secondary',
                backgroundColor: concluido ? 'primary.main' : 'action.hover',
            }}
        >
            {concluido ? <CheckIcon fontSize="small" /> : bloqueado ? <LockOutlinedIcon fontSize="small" /> : ordem}
        </Box>
    )
}

function StatusLabel({ status, regra, selecionados }: { status: PassoStatus; regra: string; selecionados: number }): JSX.Element {
    if (status === 'concluido') {
        return (
            <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                {selecionados} {selecionados === 1 ? 'selecionado' : 'selecionados'}
            </Typography>
        )
    }

    return (
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
            {status === 'bloqueado' ? 'Em breve' : regra}
        </Typography>
    )
}

function StepAccordionItem({ ordem, titulo, regra, apoio, status, selecionados, onClick, children }: StepAccordionItemProps): JSX.Element {
    const ativo = status === 'ativo'
    const bloqueado = status === 'bloqueado'

    return (
        <Paper
            elevation={0}
            sx={{
                borderRadius: 3,
                border: '1.5px solid',
                borderColor: ativo ? 'primary.main' : 'divider',
                overflow: 'hidden',
                opacity: bloqueado ? 0.6 : 1,
                boxShadow: ativo ? (theme) => `0 0 0 4px ${alpha(theme.palette.primary.main, 0.12)}` : 'none',
                transition: 'border-color 0.2s, box-shadow 0.2s',
            }}
        >
            <Stack
                direction="row"
                alignItems="center"
                spacing={1.5}
                onClick={ativo || bloqueado ? undefined : onClick}
                sx={{
                    p: 2,
                    cursor: ativo || bloqueado ? 'default' : 'pointer',
                    userSelect: 'none',
                }}
            >
                <Badge status={status} ordem={ordem} />

                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
                        {titulo}
                    </Typography>

                    {ativo && (
                        <Typography variant="body2" color="text.secondary">
                            {apoio ?? regra}
                        </Typography>
                    )}
                </Box>

                {!ativo && <StatusLabel status={status} regra={regra} selecionados={selecionados} />}
            </Stack>

            <Collapse in={ativo} unmountOnExit>
                <Stack spacing={1.5} sx={{ px: 2, pb: 2 }}>
                    {children}
                </Stack>
            </Collapse>
        </Paper>
    )
}

export default StepAccordionItem